/**
 * Mastery Week 2 (W2) — handleLongAbsence
 *
 * Demote (box - 1, floor 1) every KP when the student has been absent
 * for more than 7 days since their most recent review.
 *
 * Rule (per task spec, derived from research §6 Edge Case 1):
 *   - if max(lastReviewedISO across all KPs) < now - 7d  → demote ALL KPs
 *   - otherwise no-op (return a shallow-cloned array)
 *   - never mutate input or its elements
 *   - design choice: step-back by 1 (not reset to 1) so returning kids
 *     don't see every box turn red on day one (research §5 注意力斷崖).
 *
 * INVARIANTS:
 *   - pure function; never mutates inputs.
 *   - 學習與遊戲分離 — no game/ imports.
 */

import type { LeitnerBox, StudentKP } from '../../shared/types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ABSENCE_THRESHOLD_MS = 7 * MS_PER_DAY;

/** Parse ISO timestamp to epoch ms; '' / invalid → NaN. */
function parseIso(iso: string): number {
  if (!iso) return Number.NaN;
  return Date.parse(iso);
}

/**
 * Step-back box by 1, clamped to the Leitner [1, 5] floor.
 */
function stepBack(box: LeitnerBox): LeitnerBox {
  if (box <= 1) return 1;
  return (box - 1) as LeitnerBox;
}

/**
 * If max(lastReviewedISO) is strictly older than (now - 7d), return a NEW array
 * with every KP demoted by one box (floor = 1). Otherwise return a shallow
 * clone with the same elements.
 *
 * @param kps  immutable input
 * @param now  epoch ms (inject for tests)
 */
export function handleLongAbsence(
  kps: ReadonlyArray<StudentKP>,
  now: number,
): StudentKP[] {
  if (kps.length === 0) return [];

  // Compute the most recent review time across all KPs.
  let mostRecent = Number.NEGATIVE_INFINITY;
  let everReviewed = false;
  for (const kp of kps) {
    const t = parseIso(kp.lastReviewedISO);
    if (Number.isFinite(t)) {
      everReviewed = true;
      if (t > mostRecent) mostRecent = t;
    }
  }

  // No one has ever reviewed → no absence to punish (treat as fresh start).
  if (!everReviewed) return kps.slice();

  const absentFor = now - mostRecent;
  if (absentFor <= ABSENCE_THRESHOLD_MS) {
    // Under 7-day threshold → no-op clone.
    return kps.slice();
  }

  // Over threshold → demote every KP by 1 (floor 1).
  return kps.map((kp) => ({
    ...kp,
    box: stepBack(kp.box),
  }));
}
